import React, { useCallback, useMemo, useEffect } from 'react';
import { ResponseErrorPanel, TableColumn } from '@backstage/core-components';
import { CopyableText } from '../../CopyableText';
import { ModernResourceView } from '../../ModernResourceView';
import { AwsActionMenu, AwsActionConfirmationDialog } from '../actions';
import { AsgTableData } from './types';
import { mapResourcesToAsgTableData } from './utils';
import { ExecuteActionOptions } from '../../../api';
import {
  AsgResourceDetails,
  CRDAWSConfig,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { useApi, errorApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useActionManagement } from './useActionManagement';
import { useAwsContext } from '../AwsContext';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

interface AsgTableProps {
  entityRef: string;
  awsConfig: CRDAWSConfig;
  title: string;
}

export const AsgTable: React.FC<AsgTableProps> = ({
  entityRef,
  awsConfig,
  title,
}) => {
  const { api, projectCode, refreshContext } = useAwsContext();

  const executeAction = useCallback(
    (options: ExecuteActionOptions) => {
      return api.executeAction(options);
    },
    [api],
  );

  const {
    value: resources,
    loading: isLoading,
    error: error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return [];
    return api.listResources({
      serviceType: 'aws',
      resourceType: 'asg',
      projectCode: projectCode,
      entityRef: entityRef,
      cloudConfig: awsConfig,
    });
  }, [projectCode, api]);

  const handleRefresh = () => {
    // Invalidate cache for this specific AWS ASG configuration
    api.invalidateCache('listResources');
    refresh();
  };

  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const {
    menuPosition,
    confirmationDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    setConfirmationDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    errorApi,
    alertApi,
    awsConfig,
    entityRef,
    projectCode,
  });

  useEffect(() => {
    if (!refreshContext) return;

    const refreshKey = `aws:asg:${awsConfig.accountId}:${awsConfig.region}`;
    const unregister = refreshContext.registerRefresh(refreshKey, refresh);

    return unregister;
  }, [refreshContext, refresh, awsConfig.accountId, awsConfig.region]);

  const columns = useMemo<TableColumn<AsgTableData>[]>(
    () => [
      {
        title: 'ID',
        field: 'identifier',
        render: (row: AsgTableData) => (
          <div style={{ maxWidth: '400px' }}>
            <CopyableText value={row.identifier} />
          </div>
        ),
      },
      {
        title: 'Name',
        field: 'name',
      },
      {
        title: 'Desired',
        field: 'desired',
        type: 'numeric',
      },
      {
        title: 'Min',
        field: 'min',
        type: 'numeric',
      },
      {
        title: 'Max',
        field: 'max',
        type: 'numeric',
      },
      {
        title: 'Instance Count',
        field: 'instanceCount',
        type: 'numeric',
      },
      {
        title: 'Status',
        field: 'status',
      },
    ],
    [],
  );

  const data = useMemo(
    () =>
      resources
        ? mapResourcesToAsgTableData(resources as AsgResourceDetails[])
        : [],
    [resources],
  );

  if (error) {
    error.message = `Failed to load AWS ASGs`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <>
      <ModernResourceView<AsgTableData>
        title={title}
        data={data}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onActions={handleTableActionClick}
        cloudService="aws"
        resourceType="asg"
        columns={columns}
        colorTheme="#FF9900" // AWS Orange
        emptyMessage="No Auto Scaling Groups found"
      />

      <AwsActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={() => {}}
        menuPosition={menuPosition}
        service="asg"
      />

      <AwsActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="asg"
      />
    </>
  );
};
